import {classes} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {Icon} from '@bitstillery/common/components/ui/icon/icon'
import {watch} from '@bitstillery/mithril'
import m from '@bitstillery/mithril'
import {is_signal} from '@bitstillery/common/lib/store'

import type {FieldEditorAttrs} from '@bitstillery/common/types/field'
import {Signal} from '@bitstillery/mithril'
import type {Unwatch} from '@bitstillery/mithril'
import type Quill from 'quill'
import {failure_message} from '@bitstillery/common/lib/validation'

/** Must match `tableUpInternal.moduleName` / `blotName.tableWrapper` in quill-table-up. */
const QUILL_TABLE_UP_KEY = 'table-up'

/** The signal behind a `[state, key]` model: a state object exposes each field's signal as `$key`. */
function field_signal(model: [object, PropertyKey]): Signal<unknown> | undefined {
    const candidate: unknown = Reflect.get(model[0], `$${String(model[1])}`)
    return candidate instanceof Signal ? candidate : undefined
}

export class FieldEditor extends MithrilComponent<FieldEditorAttrs> {
    editor?: Quill
    placeholder_plugin?: {get_clean_html: () => string}
    watchers: Unwatch[] = []
    _change_from_quill = false
    _applying_placeholder_format = false

    toolbar_options = [
        ['bold', 'italic', 'underline', 'strike', {align: []}, {indent: '-1'}, {indent: '+1'}, 'clean'],
        [{[QUILL_TABLE_UP_KEY]: []}, 'image', 'link'],
        [{color: []}, {background: []}, 'blockquote', 'code-block'],
        [{list: 'ordered'}, {list: 'bullet'}, {list: 'check'}],
        [{script: 'sub'}, {script: 'super'}],
        [{header: [1, 2, 3, 4, 5, 6, false]}],
        [{font: []}],
        [],
    ]

    override oninit(vnode: m.Vnode<FieldEditorAttrs>) {
        const {model} = vnode.attrs
        const signal: Signal<unknown> | undefined = is_signal(model) ? model : field_signal(model)
        if (signal) {
            this.watchers.push(
                watch(signal, (value) => {
                    if (this._change_from_quill) {
                        this._change_from_quill = false
                        return
                    }
                    // On model change, load content into the editor, using clipboard matcher
                    this._change_from_quill = true
                    if (this.editor) {
                        this.editor.setText('') // Clear first
                        this.editor.clipboard.dangerouslyPasteHTML(0, typeof value === 'string' ? value : '')
                    }
                    this._change_from_quill = false
                }),
            )
        }
    }

    override onremove() {
        this.watchers.map((unwatch) => unwatch())
    }

    override oncreate(vnode: m.VnodeDOM<FieldEditorAttrs>) {
        const toolbar_options = [...this.toolbar_options, vnode.attrs.toolbar ? vnode.attrs.toolbar : undefined]
        const editor_element = vnode.dom.querySelector<HTMLElement>('.editor')
        if (!editor_element) {
            return
        }
        if (typeof document === 'undefined') {
            return
        }

        void import('./field_editor_quill').then(({init_field_editor_quill}) =>
            init_field_editor_quill(this, vnode, editor_element, toolbar_options),
        )
    }

    view(vnode: m.Vnode<FieldEditorAttrs>) {
        if (this.editor && !vnode.attrs.disabled) {
            this.editor.enable(true)
        } else if (this.editor && vnode.attrs.disabled) {
            this.editor.disable()
        }
        const {validation} = vnode.attrs
        const invalid = validation ? validation._invalid : false

        // Add human readable description to the validation object.
        if (validation && vnode.attrs.label) {
            validation.description = vnode.attrs.label
        }

        return (
            <div
                className={classes('c-field-editor field', vnode.attrs.className, {
                    disabled: vnode.attrs.disabled || vnode.attrs.loading,
                    invalid: validation && validation._invalid && validation.dirty,
                    valid: !validation || !invalid,
                })}
            >
                {vnode.attrs.label && (
                    <label>
                        {vnode.attrs.label}
                        {vnode.attrs.icon && <Icon name={vnode.attrs.icon} />}
                        {validation && <span className='validation'>{validation.label}</span>}
                    </label>
                )}

                <div className='editor'></div>
                {(() => {
                    if (invalid && validation?.dirty) {
                        return <div className='help validation'>{failure_message(invalid)}</div>
                    } else if (vnode.attrs.help) {
                        return <div class='help'>{vnode.attrs.help}</div>
                    }
                    return undefined
                })()}
            </div>
        )
    }
}
